// shared/src/oracle/macro-dash-bundle.ts
// The /oracle "Macro" tab read contract (design doc §5.3/§9, P4). #79 slice 2: this tab now renders
// the CONSUMED oracle_macro_series superset — the exact computed.macro block the brief reads via the
// gateway (FRED-derived fields PLUS the Group-C context: crypto dominance, Fear&Greed, Deribit vol,
// VIX/DXY) — instead of the redundant oracle_canonical data_type='macro' shadow (19 raw FRED series,
// UNCONSUMED). Each row is one BLOCK FIELD. FRED-derived fields carry display provenance (issuer,
// status, corroborators) joined from the fire's authoritative resolution (series columns, migration
// 0110); Group-C fields are single-source → 'single_source' status, no issuer/corroboration.
// Defined ONCE here so the api-worker projector and the apps/web tab share it verbatim (no C035
// hand-mirror). The projector runs the SQL; this PURE assembler shapes it. Display-only, not an LLM input.

import { z } from 'zod';
import type { OracleSourceRow, OraclePolicyRow, OracleObservationRow } from './oracle-store.js';
import type { OracleMacroSeriesRow, MacroSeriesRunAgg } from './macro-series-store.js';
import { decodeMacroField } from './macro-series-store.js';
import { macroFieldLabel, macroFamilyRank } from './macro-field-label.js';
import { FRED_FIELD_SERIES } from './macro-block.js';

// ─────────────────────────── view schemas ───────────────────────────

/** The macro AUTHORITATIVE policy, surfaced for the Macro tab header. */
export const MacroPolicyViewSchema = z.object({
  mode:                  z.literal('authoritative'),
  corroboration_tol_pct: z.number().nullable(),
  policy_version:        z.string(),
  updated_at:            z.string(),
  updated_by:            z.string().nullable(),
});

/** The FRED issuer source config + health over the window. */
export const MacroSourceViewSchema = z.object({
  source_id:        z.string(),
  enabled:          z.boolean(),
  tier:             z.enum(['A', 'B', 'C']),
  poll_interval_s:  z.number(),
  staleness_max_s:  z.number(),
  updated_at:       z.string(),
  updated_by:       z.string().nullable(),
  last_fetched_at:  z.string().nullable(),
  obs_count:        z.number(),   // observations in the window
  error_count:      z.number(),   // errored observations in the window
});

/** One macro BLOCK FIELD: its label metadata + the consumed value + display provenance. value is
 *  number|string (the block is scalar — enums like 'NORMAL', suffixed strings like '3.40T'). status
 *  'single_source' = a Group-C field (no authoritative resolution); issuer/n_corroborators are then null. */
export const MacroSeriesViewSchema = z.object({
  key:             z.string(),   // computed.macro block field (vix, cpi_yoy, fear_greed, …)
  label:           z.string(),   // human label (never a bare field id — translation-in-code)
  unit:            z.string(),
  family:          z.string(),
  value:           z.union([z.number(), z.string()]).nullable(),
  observed_at:     z.string().nullable(),  // the fire's world-truth instant
  age_s:           z.number().nullable(),  // now − observed_at (freshness)
  stale:           z.boolean(),            // age_s > the source's staleness_max_s (informational for macro)
  status:          z.enum(['ok', 'below_quorum', 'no_candidates', 'pending', 'single_source']),
  issuer:          z.string().nullable(),  // resolved issuer (FRED fields); null for single-source
  n_corroborators: z.number().nullable(),  // used.length − 1 (FRED fields); null for single-source
  run_id:          z.string().nullable(),
  created_at:      z.string().nullable(),  // when this fire wrote the field
});

/** A recent macro fire (one row per fire, aggregated). */
export const MacroRunViewSchema = z.object({
  run_id:       z.string(),
  created_at:   z.string(),
  series_total: z.number(),
  series_ok:    z.number(),
});

export const MacroDashBundleSchema = z.object({
  data_type:    z.literal('macro'),
  generated_at: z.string(),
  window_h:     z.number(),
  policy:       MacroPolicyViewSchema.nullable(),
  source:       MacroSourceViewSchema.nullable(),
  series:       z.array(MacroSeriesViewSchema),
  runs:         z.array(MacroRunViewSchema),
});

export type MacroPolicyView   = z.infer<typeof MacroPolicyViewSchema>;
export type MacroSourceView   = z.infer<typeof MacroSourceViewSchema>;
export type MacroSeriesView   = z.infer<typeof MacroSeriesViewSchema>;
export type MacroRunView      = z.infer<typeof MacroRunViewSchema>;
export type MacroDashBundle   = z.infer<typeof MacroDashBundleSchema>;

// ─────────────────────────── pure assembler ───────────────────────────

export interface MacroAssembleInput {
  readonly nowMs:       number;
  readonly generatedAt: string;
  readonly windowH:     number;
  readonly policy:      OraclePolicyRow | null;
  readonly sources:     ReadonlyArray<OracleSourceRow>;          // macro sources (the fred issuer)
  readonly seriesRows:  ReadonlyArray<OracleMacroSeriesRow>;     // the LATEST fire's block-field rows
  readonly runs:        ReadonlyArray<MacroSeriesRunAgg>;        // recent fires (aggregate), newest first
  readonly windowObservations: ReadonlyArray<OracleObservationRow>; // fred obs in the window (source health)
}

const ERROR_RE = /error/i;

export function assembleMacroDashBundle(input: MacroAssembleInput): MacroDashBundle {
  const { nowMs, generatedAt, windowH, policy, sources, seriesRows, runs, windowObservations } = input;

  // ── policy view (only surfaced when it is genuinely authoritative) ──
  const policyView: MacroPolicyView | null =
    policy && policy.mode === 'authoritative'
      ? {
          mode: 'authoritative',
          corroboration_tol_pct: policy.corroboration_tol_pct,
          policy_version: policy.policy_version,
          updated_at: policy.updated_at,
          updated_by: policy.updated_by,
        }
      : null;

  // ── source view (the fred issuer) + window health ──
  const fred = sources.find((s) => s.source_id === 'fred') ?? sources[0] ?? null;
  let sourceView: MacroSourceView | null = null;
  if (fred) {
    const fredObs = windowObservations.filter((o) => o.source_id === fred.source_id);
    const lastFetched = fredObs.reduce<string | null>((a, o) => (a == null || o.fetched_at > a ? o.fetched_at : a), null);
    sourceView = {
      source_id: fred.source_id,
      enabled: fred.enabled === 1,
      tier: fred.tier,
      poll_interval_s: fred.poll_interval_s,
      staleness_max_s: fred.staleness_max_s,
      updated_at: fred.updated_at,
      updated_by: fred.updated_by,
      last_fetched_at: lastFetched,
      obs_count: fredObs.length,
      error_count: fredObs.filter((o) => o.value == null || o.quality.split('"').some((q) => ERROR_RE.test(q))).length,
    };
  }
  const stalenessMax = fred?.staleness_max_s ?? Infinity;

  // ── series rows: the latest fire's block-field superset (FRED-derived + Group-C) ──
  const series: MacroSeriesView[] = seriesRows.map((r): MacroSeriesView => {
    const { label, unit, family } = macroFieldLabel(r.field);
    const obsMs = r.observed_at ? Date.parse(r.observed_at) : NaN;
    const age_s = Number.isFinite(obsMs) ? Math.round((nowMs - obsMs) / 1000) : null;
    const isFred = r.field in FRED_FIELD_SERIES;
    const status: MacroSeriesView['status'] = isFred
      ? ((r.status as MacroSeriesView['status'] | null) ?? 'pending')
      : 'single_source';
    return {
      key: r.field, label, unit, family,
      value: decodeMacroField(r.value_num, r.value_text),
      observed_at: r.observed_at,
      age_s,
      stale: age_s != null && age_s > stalenessMax,
      status,
      issuer: isFred ? r.issuer : null,
      n_corroborators: isFred ? (r.n_corroborators ?? 0) : null,
      run_id: r.run_id,
      created_at: r.created_at,
    };
  }).sort((a, b) =>
    macroFamilyRank(a.family) - macroFamilyRank(b.family)
    || a.family.localeCompare(b.family)
    || a.key.localeCompare(b.key));

  // ── recent runs (aggregate per fire) ──
  const runsView: MacroRunView[] = runs.map((r) => ({
    run_id: r.run_id, created_at: r.created_at, series_total: r.series_total, series_ok: r.series_ok,
  }));

  return { data_type: 'macro', generated_at: generatedAt, window_h: windowH, policy: policyView, source: sourceView, series, runs: runsView };
}
